import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { FieldStack } from '@/components/FieldStack'
import { Field } from '@/components/Field'
import { Button } from '@/components/Button'
import { Strip } from '@/components/Strip'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { AUTH_BRAND, A_CHANGEPW_COPY } from '@/copy/registry'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

/** `a-changepw` — a signed-in change. `identityChangePassword` ends every session including the
 * caller's own (§3.1f, §3.1i rule 7); the client always hands off to sign-in. */
export function AChangepw({ onChanged, onCancel }: { onChanged: () => void; onCancel: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const change = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/password/change', {
        body: { current_password: currentPassword, new_password: newPassword },
      })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: onChanged,
  })

  const problem = change.error instanceof ApiProblemError ? change.error.problem : undefined
  const mismatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword !== confirmPassword

  return (
    <AuthCard unitId={unitId('a-changepw')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_CHANGEPW_COPY.heading}</h3>
      <AuthCardLead>{A_CHANGEPW_COPY.lead}</AuthCardLead>

      <FieldStack>
        <Field label={A_CHANGEPW_COPY.currentPasswordLabel}>
          <input
            type="password"
            className={TEXT_INPUT_CLASSNAME}
            style={TEXT_INPUT_STYLE}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </Field>
        <Field label={A_CHANGEPW_COPY.newPasswordLabel}>
          <input type="password" className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        </Field>
        <Field label={A_CHANGEPW_COPY.confirmPasswordLabel}>
          <input
            type="password"
            className={TEXT_INPUT_CLASSNAME}
            style={TEXT_INPUT_STYLE}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </Field>
      </FieldStack>

      <div className="my-9">
        <Strip>{A_CHANGEPW_COPY.everySessionEndsNote}</Strip>
      </div>

      {mismatch && <FormError>{A_CHANGEPW_COPY.passwordMismatch}</FormError>}
      {problem && <AChangepwRefusal problem={problem} />}

      <Button
        data-act={actionId('session.changePassword')}
        full
        disabled={change.isPending || mismatch || !currentPassword || !newPassword || !confirmPassword}
        onClick={() => change.mutate()}
      >
        {change.isPending ? 'Changing…' : A_CHANGEPW_COPY.submitButton}
      </Button>

      <p className="mt-14">
        <a href="#" data-act={actionId('nav.a-signin')} onClick={(e) => { e.preventDefault(); onCancel() }} className="text-link text-small">
          {A_CHANGEPW_COPY.cancelLabel}
        </a>
      </p>
    </AuthCard>
  )
}

function AChangepwRefusal({ problem }: { problem: Problem }) {
  switch (problem.refusal_kind) {
    case 'CREDENTIAL_REJECTED':
      return <FormError>{A_CHANGEPW_COPY.rejectedCopy}</FormError>
    case 'VALIDATION_FAILED':
      return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
    case 'RATE_LIMITED':
      return <RateLimitedNotice problem={problem} />
    default:
      return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
  }
}
